import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CircularProgress } from '@material-ui/core';
import './StaffManagement.css';
import config from '../config';

function StaffManagement() {
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({
        userType: 'staff',
        userFullName: '',
        employeeId: '',
        department: '',
        designation: '',
        email: '',
        password: '',
        mobileNumber: '',
        permissions: {
            canUploadPDF: false,
            canManageUsers: false,
            canManageBooks: false
        }
    });
    const API_URL = config.API_URL;

    // Define fetchStaff function using useCallback to prevent unnecessary re-renders
    const fetchStaff = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}api/users/staff`);
            setStaff(response.data);
            setLoading(false);
        } catch (err) {
            setError('Error fetching staff members');
            setLoading(false);
        }
    }, [API_URL]);

    // Use fetchStaff in the dependency array since it's now memoized with useCallback
    useEffect(() => {
        fetchStaff();
    }, [fetchStaff]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name.startsWith('permissions.')) {
            const permissionName = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                permissions: {
                    ...prev.permissions,
                    [permissionName]: e.target.checked
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await axios.post(`${API_URL}api/users/create-staff`, {
                ...formData,
                isAdmin: true // This should come from auth context in production
            });
            fetchStaff();
            setFormData({
                userType: 'staff',
                userFullName: '',
                employeeId: '',
                department: '',
                designation: '',
                email: '',
                password: '',
                mobileNumber: '',
                permissions: {
                    canUploadPDF: false,
                    canManageUsers: false,
                    canManageBooks: false
                }
            });
        } catch (err) {
            setError(err.response?.data || 'Error creating staff member');
        } finally {
            setLoading(false);
        }
    };

    const handlePermissionUpdate = async (userId, permissions) => {
        try {
            await axios.put(`${API_URL}api/users/update-permissions/${userId}`, {
                ...permissions,
                isAdmin: true // This should come from auth context in production
            });
            fetchStaff();
        } catch (err) {
            setError('Error updating permissions');
        }
    };

    return (
        <div className="staff-management">
            <div className="staff-form-container">
                <h2>Add New Staff Member</h2>
                <form onSubmit={handleSubmit} className="staff-form">
                    <select 
                        id="staff-user-type"
                        name="userType" 
                        value={formData.userType}
                        onChange={handleInputChange}
                    >
                        <option value="staff">Staff</option>
                        <option value="employee">Employee</option>
                    </select>

                    <input
                        id="staff-full-name"
                        type="text"
                        name="userFullName"
                        placeholder="Full Name"
                        value={formData.userFullName}
                        onChange={handleInputChange}
                        required
                    />

                    <input
                        id="staff-employee-id"
                        type="text"
                        name="employeeId"
                        placeholder="Employee ID"
                        value={formData.employeeId}
                        onChange={handleInputChange}
                        required
                    />

                    <input
                        id="staff-department"
                        type="text"
                        name="department"
                        placeholder="Department"
                        value={formData.department}
                        onChange={handleInputChange}
                    />

                    <input
                        id="staff-designation"
                        type="text"
                        name="designation"
                        placeholder="Designation"
                        value={formData.designation}
                        onChange={handleInputChange}
                    />

                    <input
                        id="staff-email"
                        type="email"
                        name="email"
                        placeholder="Email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                    />

                    <input
                        id="staff-password"
                        type="password"
                        name="password"
                        placeholder="Password"
                        value={formData.password}
                        onChange={handleInputChange}
                        required
                    />

                    <input
                        id="staff-mobile-number"
                        type="tel"
                        name="mobileNumber"
                        placeholder="Mobile Number"
                        value={formData.mobileNumber}
                        onChange={handleInputChange}
                        required
                    />

                    <div className="permissions-container">
                        <h3>Permissions</h3>
                        <label htmlFor="permission-upload-pdf">
                            <input
                                id="permission-upload-pdf"
                                type="checkbox"
                                name="permissions.canUploadPDF"
                                checked={formData.permissions.canUploadPDF}
                                onChange={handleInputChange}
                            />
                            Can Upload PDF
                        </label>
                        <label htmlFor="permission-manage-users">
                            <input
                                id="permission-manage-users"
                                type="checkbox"
                                name="permissions.canManageUsers"
                                checked={formData.permissions.canManageUsers}
                                onChange={handleInputChange}
                            />
                            Can Manage Users
                        </label>
                        <label htmlFor="permission-manage-books">
                            <input
                                id="permission-manage-books"
                                type="checkbox"
                                name="permissions.canManageBooks"
                                checked={formData.permissions.canManageBooks}
                                onChange={handleInputChange}
                            />
                            Can Manage Books
                        </label>
                    </div>

                    <button id="staff-submit-button" type="submit" disabled={loading}>
                        {loading ? <CircularProgress size={24} /> : 'Add Staff Member'}
                    </button>
                </form>
            </div>

            <div className="staff-list-container">
                <h2>Staff Members</h2>
                {loading ? (
                    <div className="loading-container">
                        <CircularProgress />
                    </div>
                ) : error ? (
                    <div className="error-message">{error}</div>
                ) : (
                    <div className="staff-list">
                        {staff.map(member => (
                            <div key={member._id} className="staff-card">
                                <div className="staff-info">
                                    <h3>{member.userFullName}</h3>
                                    <p>ID: {member.employeeId}</p>
                                    <p>Type: {member.userType}</p>
                                    <p>Department: {member.department}</p>
                                    <p>Designation: {member.designation}</p>
                                </div>
                                <div className="staff-permissions">
                                    <h4>Permissions</h4>
                                    <label htmlFor={`upload-pdf-${member._id}`}>
                                        <input
                                            id={`upload-pdf-${member._id}`}
                                            type="checkbox"
                                            checked={member.permissions?.canUploadPDF || false}
                                            onChange={(e) => handlePermissionUpdate(member._id, {
                                                ...member.permissions,
                                                canUploadPDF: e.target.checked
                                            })}
                                        />
                                        Upload PDF
                                    </label>
                                    <label htmlFor={`manage-users-${member._id}`}>
                                        <input
                                            id={`manage-users-${member._id}`}
                                            type="checkbox"
                                            checked={member.permissions?.canManageUsers || false}
                                            onChange={(e) => handlePermissionUpdate(member._id, {
                                                ...member.permissions,
                                                canManageUsers: e.target.checked
                                            })}
                                        />
                                        Manage Users
                                    </label>
                                    <label htmlFor={`manage-books-${member._id}`}>
                                        <input
                                            id={`manage-books-${member._id}`}
                                            type="checkbox"
                                            checked={member.permissions?.canManageBooks || false}
                                            onChange={(e) => handlePermissionUpdate(member._id, {
                                                ...member.permissions,
                                                canManageBooks: e.target.checked
                                            })}
                                        />
                                        Manage Books
                                    </label>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default StaffManagement; 